import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { Select } from '@/components/Select'
import { useAuth } from '@/hooks/useAuth'
import { useCampaignDashboard } from '@/hooks/useCampaignDashboard'
import { useInstitutionCampaignSelect } from '@/hooks/useInstitutionCampaignSelect'
import { useTermsAcceptanceRequired } from '@/hooks/useTermsAcceptanceRequired'
import { canViewCampaignDashboard } from '@/lib/collaborator-permissions'
import {
  calculateDelta,
  deriveDashboardInsights,
  formatDeltaPercentLabel,
  formatChartDay,
  formatCompactDate,
  formatCurrency,
  formatDateInput,
  formatDateTime,
  formatNumber,
  getHomeDashboardPeriodRange,
  type HomeDashboardPeriodId,
} from '@/lib/campaign-dashboard'
import type { CampaignSalesChartPoint } from '@/types/campaign-report'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import styles from './HomePage.module.css'

const PERIODS: { id: HomeDashboardPeriodId; label: string }[] = [
  { id: '30d', label: '30d' },
  { id: '7d', label: '7d' },
  { id: '1a', label: '1a' },
  { id: 'hoje', label: 'Hoje' },
  { id: 'all', label: 'All' },
]

function sumRevenueOnDate(points: CampaignSalesChartPoint[], dayYmd: string): number {
  return points
    .filter((p) => p.date === dayYmd)
    .reduce((acc, p) => acc + p.amount, 0)
}

function sumPaymentsOnDate(points: CampaignSalesChartPoint[], dayYmd: string): number {
  return points
    .filter((p) => p.date === dayYmd)
    .reduce((acc, p) => acc + p.payments_count, 0)
}

export function HomePage() {
  const needsTermsAcceptance = useTermsAcceptanceRequired()
  const { isCollaborator, hasPermission } = useAuth()
  const canViewDashboard = canViewCampaignDashboard(Boolean(isCollaborator), hasPermission)

  const {
    campaignOptions,
    campaignValue,
    handleCampaignValueChange,
    selectPlaceholder,
    campaignsError,
    persistError,
    isSelectorDisabled,
    campaignId,
    selectedCampaign,
  } = useInstitutionCampaignSelect({ enabled: !needsTermsAcceptance && canViewDashboard })

  const [periodoRecap, setPeriodoRecap] = useState<HomeDashboardPeriodId>('30d')
  const [modoGrafico, setModoGrafico] = useState<'barras' | 'linhas'>('barras')

  const { dateFrom, dateTo } = useMemo(
    () =>
      getHomeDashboardPeriodRange(
        periodoRecap,
        selectedCampaign?.starts_at ?? null
      ),
    [periodoRecap, selectedCampaign?.starts_at]
  )

  const dashboard = useCampaignDashboard({
    campaignId,
    dateFrom,
    dateTo,
    enabled: !needsTermsAcceptance && canViewDashboard,
  })

  const insights = useMemo(
    () => (dashboard.data ? deriveDashboardInsights(dashboard.data) : null),
    [dashboard.data]
  )

  const summary = dashboard.data?.summary
  const prevSummary = dashboard.previousData?.summary
  const chartPoints = dashboard.data?.sales_chart.points ?? []

  const todayYmd = formatDateInput(new Date())
  const revenueToday = sumRevenueOnDate(chartPoints, todayYmd)
  const paymentsToday = sumPaymentsOnDate(chartPoints, todayYmd)

  const dYesterday = new Date()
  dYesterday.setDate(dYesterday.getDate() - 1)
  const yesterdayYmd = formatDateInput(dYesterday)

  const revenueYesterdayInRange =
    yesterdayYmd >= dateFrom && yesterdayYmd <= dateTo
      ? sumRevenueOnDate(chartPoints, yesterdayYmd)
      : null
  const revenueTodayDelta = calculateDelta(
    revenueToday,
    revenueYesterdayInRange !== null ? revenueYesterdayInRange : null
  )

  const deltaRevenue = calculateDelta(
    summary?.total_revenue ?? 0,
    prevSummary?.total_revenue ?? null
  )
  const deltaTicket = calculateDelta(
    summary?.average_ticket ?? 0,
    prevSummary?.average_ticket ?? null
  )
  const deltaPayments = calculateDelta(
    summary?.total_payments ?? 0,
    prevSummary?.total_payments ?? null
  )

  const totalNumbers = dashboard.data?.campaign.total_numbers
  const soldNumbers = summary?.total_quantity ?? 0
  const maxMeta =
    totalNumbers != null && totalNumbers > 0
      ? Math.max(totalNumbers, soldNumbers, 1)
      : Math.max(soldNumbers, 1)

  const topCampanhaRows = useMemo(() => {
    const sel = campaignOptions.find((o) => o.value === campaignValue)
    const nome = sel?.label ?? 'Campanha'
    const qtd = summary?.total_payments ?? 0
    const max = Math.max(qtd, 1)
    return [{ nome, qtd, max }]
  }, [campaignOptions, campaignValue, summary?.total_payments])

  const donorsTodayCount = useMemo(() => {
    if (!dashboard.data?.recent_donors) return 0
    return dashboard.data.recent_donors.filter((d) => {
      if (!d.last_payment_paid_at) return false
      const paid = new Date(d.last_payment_paid_at)
      if (Number.isNaN(paid.getTime())) return false
      return formatDateInput(paid) === todayYmd
    }).length
  }, [dashboard.data?.recent_donors, todayYmd])

  const periodLabel = dashboard.data
    ? `${formatCompactDate(dashboard.data.sales_chart.date_from)} – ${formatCompactDate(dashboard.data.sales_chart.date_to)}`
    : `${formatCompactDate(dateFrom)} – ${formatCompactDate(dateTo)}`

  const showDashboardSkeleton =
    dashboard.isLoading && !dashboard.data && campaignId !== null

  return (
    <div className={styles.page}>
      <header className={styles.topHeader}>
        <div className={styles.topHeaderText}>
          <h1 className={styles.title}>Dashboard</h1>
          <p className={styles.subtitle}>Visão geral de vendas e desempenho</p>
        </div>
        <div className={styles.campaignSelector}>
          <Select
            id="campanha-select"
            value={campaignValue}
            onChange={handleCampaignValueChange}
            options={campaignOptions}
            placeholder={selectPlaceholder}
            label="Campanha"
            icon="mdi:bullhorn"
            disabled={isSelectorDisabled}
          />
          {campaignsError ? (
            <p className={styles.campaignSelectorError} role="alert">
              {campaignsError}
            </p>
          ) : null}
          {persistError ? (
            <p className={styles.campaignSelectorError} role="alert">
              {persistError}
            </p>
          ) : null}
        </div>
      </header>

      {!canViewDashboard ? (
        <section className={styles.recapCard} role="status">
          <h2 className={styles.recapTitle}>Dashboard indisponível</h2>
          <p className={styles.recapPeriodLabel}>
            Você não tem permissão para visualizar as métricas das campanhas. Peça ao dono da
            instituição para habilitar a permissão Visualizar dashboard e métricas.
          </p>
        </section>
      ) : null}

      {canViewDashboard && dashboard.error && campaignId !== null ? (
        <div className={styles.campaignSelectorError} role="alert">
          {dashboard.error}
        </div>
      ) : null}

      {canViewDashboard ? (
        <>
      <section className={styles.kpiRow} aria-label="Indicadores principais">
        <article className={styles.kpiCard}>
          <div className={`${styles.kpiIconWrap} ${styles.kpiIconBlue}`}>
            <Icon icon="mdi:numeric" aria-hidden />
          </div>
          <div className={styles.kpiBody}>
            <p className={styles.kpiTitle}>Números vendidos</p>
            <p className={styles.kpiValue}>
              {showDashboardSkeleton
                ? '…'
                : formatNumber(summary?.total_quantity ?? 0)}
            </p>
          </div>
        </article>
        <article className={styles.kpiCard}>
          <div className={`${styles.kpiIconWrap} ${styles.kpiIconGreen}`}>
            <Icon icon="mdi:cart-outline" aria-hidden />
          </div>
          <div className={styles.kpiBody}>
            <p className={styles.kpiTitle}>Pagamentos</p>
            <p className={styles.kpiValue}>
              {showDashboardSkeleton
                ? '…'
                : formatNumber(summary?.total_payments ?? 0)}
            </p>
          </div>
        </article>
        <article className={styles.kpiCard}>
          <div className={`${styles.kpiIconWrap} ${styles.kpiIconYellow}`}>
            <Icon icon="mdi:account-group-outline" aria-hidden />
          </div>
          <div className={styles.kpiBody}>
            <p className={styles.kpiTitle}>Compradores (período)</p>
            <p className={styles.kpiValue}>
              {showDashboardSkeleton
                ? '…'
                : formatNumber(summary?.total_donors ?? 0)}
            </p>
          </div>
        </article>
        <article className={styles.kpiCard}>
          <div className={`${styles.kpiIconWrap} ${styles.kpiIconRed}`}>
            <Icon icon="mdi:cash-multiple" aria-hidden />
          </div>
          <div className={styles.kpiBody}>
            <p className={styles.kpiTitle}>Ticket médio</p>
            <p className={styles.kpiValue}>
              {showDashboardSkeleton
                ? '…'
                : formatCurrency(summary?.average_ticket ?? 0)}
            </p>
          </div>
        </article>
        <article className={styles.kpiCard}>
          <div className={`${styles.kpiIconWrap} ${styles.kpiIconPurple}`}>
            <Icon icon="mdi:tag-outline" aria-hidden />
          </div>
          <div className={styles.kpiBody}>
            <p className={styles.kpiTitle}>Números restantes</p>
            <p className={styles.kpiValue}>
              {showDashboardSkeleton
                ? '…'
                : insights?.remainingNumbers != null
                  ? formatNumber(insights.remainingNumbers)
                  : '—'}
            </p>
          </div>
        </article>
        <article className={styles.kpiCard}>
          <div className={`${styles.kpiIconWrap} ${styles.kpiIconPink}`}>
            <Icon icon="mdi:chart-timeline-variant" aria-hidden />
          </div>
          <div className={styles.kpiBody}>
            <p className={styles.kpiTitle}>Receita / comprador</p>
            <p className={styles.kpiValue}>
              {showDashboardSkeleton
                ? '…'
                : insights
                  ? formatCurrency(insights.revenuePerDonor)
                  : '—'}
            </p>
          </div>
        </article>
      </section>

      <section className={styles.recapCard} aria-label="Relatório de recapitulação">
        <div className={styles.recapHeader}>
          <h3 className={styles.recapTitle}>Relatório de vendas</h3>
        </div>
        <p className={styles.recapPeriodLabel}>Período: {periodLabel}</p>

        <div className={styles.recapToolbar}>
          <div className={styles.recapPills} role="group" aria-label="Período">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`${styles.recapPill} ${periodoRecap === p.id ? styles.recapPillActive : ''}`}
                onClick={() => setPeriodoRecap(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className={styles.recapToggle} role="group" aria-label="Tipo de gráfico">
            <button
              type="button"
              className={`${styles.recapToggleBtn} ${modoGrafico === 'linhas' ? styles.recapToggleBtnActive : ''}`}
              onClick={() => setModoGrafico('linhas')}
            >
              Linhas
            </button>
            <button
              type="button"
              className={`${styles.recapToggleBtn} ${modoGrafico === 'barras' ? styles.recapToggleBtnActive : ''}`}
              onClick={() => setModoGrafico('barras')}
            >
              Barras
            </button>
          </div>
        </div>

        <div className={styles.recapBody}>
          <div className={styles.recapChartArea}>
            {showDashboardSkeleton ? (
              <p className={styles.recapPeriodLabel}>Carregando dados…</p>
            ) : chartPoints.length === 0 ? (
              <p className={styles.recapPeriodLabel}>
                Nenhum dado de venda no período selecionado.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart
                  data={chartPoints}
                  margin={{ top: 24, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-border-subtle)"
                  />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatChartDay}
                    tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                    tickFormatter={(v: number) => formatCurrency(v)}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                    tickFormatter={(v: number) => formatNumber(v)}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: '1px solid var(--color-border-subtle)',
                    }}
                    formatter={(value, name) => {
                      const n = typeof value === 'number' ? value : Number(value ?? 0)
                      if (name === 'Receita') return formatCurrency(n)
                      return formatNumber(n)
                    }}
                    labelFormatter={(value) =>
                      typeof value === 'string'
                        ? formatCompactDate(value)
                        : String(value ?? '')
                    }
                  />
                  <Legend />
                  {modoGrafico === 'barras' ? (
                    <>
                      <Bar
                        yAxisId="right"
                        dataKey="payments_count"
                        name="Pagamentos"
                        fill="var(--color-primary-blue)"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="amount"
                        name="Receita"
                        fill="#10b981"
                        radius={[4, 4, 0, 0]}
                      />
                    </>
                  ) : (
                    <>
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="payments_count"
                        name="Pagamentos"
                        stroke="var(--color-primary-blue)"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="amount"
                        name="Receita"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>

          <aside className={styles.recapGoals} aria-label="Meta da campanha">
            <h4 className={styles.recapGoalsTitle}>Meta da campanha</h4>
            <div className={styles.goalItem}>
              <p className={styles.goalLabel}>Números vendidos</p>
              <div className={styles.goalBarTrack}>
                <div
                  className={styles.goalBarFillBlue}
                  style={{
                    width: `${(Math.min(soldNumbers, maxMeta) / maxMeta) * 100}%`,
                  }}
                />
              </div>
              <p className={styles.goalValue}>
                {showDashboardSkeleton ? '…' : formatNumber(soldNumbers)}
              </p>
            </div>
            <div className={styles.goalItem}>
              <p className={styles.goalLabel}>
                {totalNumbers != null && totalNumbers > 0
                  ? 'Total de números da campanha'
                  : 'Total de números (não configurado)'}
              </p>
              <div className={styles.goalBarTrack}>
                <div
                  className={styles.goalBarFillGreen}
                  style={{
                    width:
                      totalNumbers != null && totalNumbers > 0
                        ? `${Math.min(100, (soldNumbers / totalNumbers) * 100)}%`
                        : '0%',
                  }}
                />
              </div>
              <p className={styles.goalValue}>
                {showDashboardSkeleton
                  ? '…'
                  : totalNumbers != null && totalNumbers > 0
                    ? `${formatNumber(soldNumbers)} / ${formatNumber(totalNumbers)}`
                    : '—'}
              </p>
            </div>
            {summary?.sell_through_percent != null ? (
              <p className={styles.recapPeriodLabel}>
                Conversão: {formatNumber(summary.sell_through_percent)}% dos números
              </p>
            ) : null}
          </aside>
        </div>

        <footer className={styles.recapFooter}>
          <div className={styles.recapFooterCell}>
            <p className={styles.recapFooterLabel}>Receita no período</p>
            <p className={styles.recapFooterValue}>
              {formatCurrency(summary?.total_revenue ?? 0)}
            </p>
            <p className={styles.recapFooterTrends}>
              <span
                className={
                  formatDeltaPercentLabel(deltaRevenue).up === true
                    ? styles.trendUp
                    : formatDeltaPercentLabel(deltaRevenue).up === false
                      ? styles.trendDown
                      : ''
                }
              >
                <Icon icon="mdi:trending-up" /> {formatDeltaPercentLabel(deltaRevenue).text}
              </span>
            </p>
          </div>
          <div className={styles.recapFooterCell}>
            <p className={styles.recapFooterLabel}>Receita hoje</p>
            <p className={styles.recapFooterValue}>{formatCurrency(revenueToday)}</p>
            <div className={styles.recapFooterTrends}>
              <span
                className={
                  formatDeltaPercentLabel(revenueTodayDelta).up === true
                    ? styles.trendUp
                    : formatDeltaPercentLabel(revenueTodayDelta).up === false
                      ? styles.trendDown
                      : ''
                }
              >
                <Icon icon="mdi:trending-up" />{' '}
                {formatDeltaPercentLabel(revenueTodayDelta).text}
              </span>
              <span className={styles.trendUp}>
                {paymentsToday > 0
                  ? `${formatNumber(paymentsToday)} pag. hoje`
                  : '—'}
              </span>
            </div>
          </div>
          <div className={styles.recapFooterCell}>
            <p className={styles.recapFooterLabel}>Ticket médio</p>
            <p className={styles.recapFooterValue}>
              {formatCurrency(summary?.average_ticket ?? 0)}
            </p>
            <p className={styles.recapFooterTrends}>
              <span
                className={
                  formatDeltaPercentLabel(deltaTicket).up === true
                    ? styles.trendUp
                    : formatDeltaPercentLabel(deltaTicket).up === false
                      ? styles.trendDown
                      : ''
                }
              >
                <Icon icon="mdi:trending-up" /> {formatDeltaPercentLabel(deltaTicket).text}
              </span>
            </p>
          </div>
          <div className={styles.recapFooterCell}>
            <p className={styles.recapFooterLabel}>Pedidos pagos</p>
            <p className={styles.recapFooterValue}>
              {formatNumber(summary?.total_payments ?? 0)}
            </p>
            <p className={styles.recapFooterTrends}>
              <span
                className={
                  formatDeltaPercentLabel(deltaPayments).up === true
                    ? styles.trendUp
                    : formatDeltaPercentLabel(deltaPayments).up === false
                      ? styles.trendDown
                      : ''
                }
              >
                <Icon icon="mdi:trending-up" /> {formatDeltaPercentLabel(deltaPayments).text}
              </span>
            </p>
          </div>
        </footer>
      </section>

      <section className={styles.convRow}>
        <article className={styles.convCard}>
          <h4 className={styles.convCardTitle}>Destaques do período</h4>
          <ul className={styles.convChannelList}>
            <li className={styles.convChannelItem}>
              <span>Melhor dia (receita)</span>
              <span className={styles.convMiniPct}>
                {insights?.bestRevenueDay
                  ? `${formatCompactDate(insights.bestRevenueDay.date)} · ${formatCurrency(insights.bestRevenueDay.amount)}`
                  : '—'}
              </span>
            </li>
            <li className={styles.convChannelItem}>
              <span>Pico de pagamentos</span>
              <span className={styles.convMiniPct}>
                {insights?.peakPaymentsDay
                  ? `${formatCompactDate(insights.peakPaymentsDay.date)} · ${formatNumber(insights.peakPaymentsDay.payments_count)}`
                  : '—'}
              </span>
            </li>
            <li className={styles.convChannelItem}>
              <span>Média de receita / dia</span>
              <span className={styles.convMiniPct}>
                {insights ? formatCurrency(insights.averageRevenuePerDay) : '—'}
              </span>
            </li>
          </ul>
        </article>

        <article className={styles.convCard}>
          <h4 className={styles.convCardTitle}>Campanha — volume no período</h4>
          <p className={styles.convBarAxisLabel}>Pagamentos</p>
          {topCampanhaRows.map((row) => (
            <div key={row.nome} className={styles.hBarRow}>
              <span className={styles.hBarName}>{row.nome}</span>
              <div className={styles.hBarTrack}>
                <div
                  className={styles.hBarFill}
                  style={{ width: `${(row.qtd / row.max) * 100}%` }}
                />
              </div>
              <span className={styles.hBarVal}>{row.qtd}</span>
            </div>
          ))}
        </article>

        <article className={styles.convCard}>
          <h4 className={styles.convCardTitle}>Saúde</h4>
          <ul className={styles.convChannelList}>
            {(insights?.alerts?.length ? insights.alerts : ['Nenhum alerta.']).map(
              (msg, idx) => (
                <li key={`${idx}-${msg}`} className={styles.convChannelItem}>
                  <span>{msg}</span>
                </li>
              )
            )}
          </ul>
        </article>
      </section>

      <section className={styles.bottomRow} aria-label="Compradores recentes">
        <article className={styles.registrationsCard}>
          <div className={styles.regHeader}>
            <h4 className={styles.regTitle}>Compradores recentes</h4>
            <div className={styles.regHeaderRight}>
              {donorsTodayCount > 0 ? (
                <span className={styles.regBadge}>
                  {formatNumber(donorsTodayCount)} com pagamento hoje
                </span>
              ) : null}
            </div>
          </div>
          {showDashboardSkeleton ? (
            <p className={styles.recapPeriodLabel}>Carregando…</p>
          ) : (dashboard.data?.recent_donors.length ?? 0) === 0 ? (
            <p className={styles.recapPeriodLabel}>Nenhum comprador recente.</p>
          ) : (
            <div className={styles.regGrid}>
              {dashboard.data!.recent_donors.map((u) => (
                <Link
                  key={u.donor_id}
                  to={`/clientes/${u.donor_id}/editar`}
                  className={`${styles.regUser} ${styles.regUserLink}`}
                  aria-label={`Editar cliente ${u.name}`}
                >
                  <div className={styles.regAvatar} aria-hidden>
                    <Icon icon="mdi:account-circle" />
                  </div>
                  <p className={styles.regUserName}>{u.name}</p>
                  <p className={styles.regUserDate}>
                    {u.last_payment_paid_at
                      ? formatDateTime(u.last_payment_paid_at)
                      : '—'}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </article>
      </section>
        </>
      ) : null}
    </div>
  )
}
